import { useEffect, useState } from "react";
import { FileText, ImageOff, Loader2, X } from "lucide-react";
import { useAuthStore } from "@/lib/auth";
import type { AdjuntoOut } from "./schemas";

/**
 * Thumbnail clickeable de un adjunto de ticket. Click → preview fullscreen
 * en un modal (imagen ampliada o iframe del PDF). Hace fetch del blob con
 * bearer auth (no podemos usar <img src=URL> directo porque el endpoint
 * requiere Authorization header).
 *
 * Cache simple: blob URLs persisten en un Map estático mientras la página
 * está montada. Se revocan al unmount completo (no por componente individual).
 */

// Cache módulo-level — vive mientras el módulo esté cargado
const blobCache = new Map<string, string>();

type Props = {
  ticketId: string;
  adjunto: AdjuntoOut;
  /** Tamaño del thumbnail en px (lado). Default 80. */
  size?: number;
};

export function AttachmentThumb({ ticketId, adjunto, size = 80 }: Props) {
  const [blobURL, setBlobURL] = useState<string | null>(
    blobCache.get(adjunto.id) ?? null,
  );
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const isImage = adjunto.mime.startsWith("image/");
  const isPDF = adjunto.mime === "application/pdf";

  useEffect(() => {
    if (!isImage) return;
    if (blobURL) return;
    let cancelled = false;
    setLoading(true);
    setFailed(false);

    const token = useAuthStore.getState().accessToken;
    fetch(`/api/v1/tickets/${ticketId}/adjuntos/${adjunto.id}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        blobCache.set(adjunto.id, url);
        setBlobURL(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [ticketId, adjunto.id, isImage, blobURL]);

  /// Asegura que el preview tenga blob URL (descarga si aún no lo tiene).
  async function ensureBlob(): Promise<string | null> {
    if (blobURL) return blobURL;
    if (!isImage && !isPDF) return null;
    const token = useAuthStore.getState().accessToken;
    try {
      const r = await fetch(
        `/api/v1/tickets/${ticketId}/adjuntos/${adjunto.id}/download`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        },
      );
      if (!r.ok) return null;
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      blobCache.set(adjunto.id, url);
      setBlobURL(url);
      return url;
    } catch {
      return null;
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setPreviewOpen(true)}
        className="group relative shrink-0 overflow-hidden rounded-md border border-zinc-700 bg-zinc-800 transition hover:border-zinc-500"
        style={{ width: size, height: size }}
      >
        {isImage && blobURL && (
          <img
            src={blobURL}
            alt={adjunto.filename}
            className="size-full object-cover"
          />
        )}
        {isImage && !blobURL && loading && (
          <div className="flex size-full items-center justify-center text-zinc-500">
            <Loader2 size={16} className="animate-spin" />
          </div>
        )}
        {isImage && failed && (
          <div className="flex size-full items-center justify-center text-red-400">
            <ImageOff size={20} />
          </div>
        )}
        {!isImage && (
          <div className="flex size-full flex-col items-center justify-center gap-1 px-2 text-center text-zinc-300">
            <FileText size={20} className="text-blue-400" />
            <span className="line-clamp-1 text-[10px]">
              {adjunto.filename}
            </span>
          </div>
        )}
      </button>

      {previewOpen && (
        <PreviewModal
          adjunto={adjunto}
          isImage={isImage}
          isPDF={isPDF}
          getBlob={ensureBlob}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </>
  );
}

// ── Preview modal fullscreen ─────────────────────────────────────────────
function PreviewModal({
  adjunto,
  isImage,
  isPDF,
  getBlob,
  onClose,
}: {
  adjunto: AdjuntoOut;
  isImage: boolean;
  isPDF: boolean;
  getBlob: () => Promise<string | null>;
  onClose: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    getBlob().then((u) => {
      if (active) {
        setUrl(u);
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, [getBlob]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-zinc-800 p-2 text-white hover:bg-zinc-700"
      >
        <X size={20} />
      </button>
      <div
        className="relative max-h-full max-w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {loading && (
          <div className="flex items-center gap-2 text-zinc-300">
            <Loader2 size={20} className="animate-spin" />
            Cargando…
          </div>
        )}
        {!loading && !url && (
          <div className="text-red-400">No se pudo cargar el archivo.</div>
        )}
        {!loading && url && isImage && (
          <img
            src={url}
            alt={adjunto.filename}
            className="max-h-[90vh] max-w-[90vw] rounded-md object-contain"
          />
        )}
        {!loading && url && isPDF && (
          <iframe
            src={url}
            title={adjunto.filename}
            className="size-[90vw] max-w-[1000px] rounded-md bg-white"
            style={{ height: "90vh" }}
          />
        )}
        {!loading && url && !isImage && !isPDF && (
          <a
            href={url}
            download={adjunto.filename}
            className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-500"
          >
            Descargar {adjunto.filename}
          </a>
        )}
      </div>
    </div>
  );
}

// ── Grid horizontal de adjuntos ──────────────────────────────────────────
export function AttachmentGrid({
  ticketId,
  adjuntos,
}: {
  ticketId: string;
  adjuntos: AdjuntoOut[];
}) {
  if (adjuntos.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {adjuntos.map((a) => (
        <AttachmentThumb key={a.id} ticketId={ticketId} adjunto={a} />
      ))}
    </div>
  );
}
